import React, { createContext, ReactNode, useReducer } from 'react';
import {
  SET_NEW_MEASURE,
  SET_OVERLAY,
  SET_MEASURE_SELECTED,
  SET_MEASURE_NAME,
  SET_SCORES_METHOD,
  SHOW_TABLE,
  SET_SCORES,
  RESET_SCORES,
  SET_ALL_VALUES_ENTERED,
  SET_SCORES_SUBCATEGORY,
  SET_LOADER,
  SET_TABLE_NAME,
  observation,
  SET_EDITING_MEASURE,
} from './utils/constants';
import { ScoresMethod } from './utils/type';

type MeasuresContextProviderProps = {
  children: ReactNode;
};

type MeasuresContext = {
  editingMeasure: mumms.measure | null;
  measureSelected: mumms.measureShortNames | null;
  tableSelected: mumms.measureShortNames | null;
  methodSelected: ScoresMethod | null;
  nameSelected: mumms.measureShortNames | null;
  newMeasure: boolean | null;
  allValuesEntered: boolean | null;
  anyValueEntered: boolean | null;
  isLoading: boolean | null;
  loadingOverlay: boolean;
  dispatch?: any;
  showTable: boolean | null;
  scoresState: any;
  scoresSubcategory: mumms.SubcategoryType | any;
};

type MeasuresContextState = {
  editingMeasure: mumms.measure | null;
  measureSelected: mumms.measureShortNames | null;
  tableSelected: mumms.measureShortNames | null;
  methodSelected: ScoresMethod | null;
  nameSelected: mumms.measureShortNames | null;
  newMeasure: boolean | null;
  allValuesEntered: boolean | null;
  anyValueEntered: boolean | null;
  isLoading: boolean | null;
  loadingOverlay: boolean;
  showTable: boolean | null;
  scoresState: any;
  scoresSubcategory: mumms.SubcategoryType | any;
};
const initialState = {
  editingMeasure: null,
  measureSelected: null,
  tableSelected: null,
  methodSelected: null,
  nameSelected: null,
  allValuesEntered: false,
  anyValueEntered: false,
  isLoading: false,
  newMeasure: null,
  loadingOverlay: false,
  showTable: false,
  scoresState: {},
  scoresSubcategory: {},
};

const initMeasuresContext = {
  editingMeasure: null,
  measureSelected: null,
  tableSelected: null,
  methodSelected: null,
  nameSelected: null,
  newMeasure: null,
  loadingOverlay: false,
  isLoading: false,
  allValuesEntered: false,
  anyValueEntered: false,
  showTable: false,
  scoresState: {},
  scoresSubcategory: {},
};

export const MeasuresContextReducer = (
  state: MeasuresContextState,
  action: { type: string; payload: any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_EDITING_MEASURE:
      newState.editingMeasure = action.payload;
      break;
    case SET_NEW_MEASURE:
      newState.newMeasure = action.payload;
      break;
    case SET_OVERLAY:
      newState.loadingOverlay = action.payload;
      break;
    case SET_MEASURE_SELECTED:
      newState.measureSelected = action.payload;
      break;
    case SET_MEASURE_NAME:
      newState.nameSelected = action.payload;
      break;
    case SET_SCORES_METHOD:
      newState.methodSelected = action.payload;
      break;
    case SET_SCORES_SUBCATEGORY:
      newState.scoresSubcategory = action.payload;
      break;
    case SHOW_TABLE:
      newState.showTable = action.payload;
      break;
    case SET_LOADER:
      newState.isLoading = action.payload;
      break;
    case SET_TABLE_NAME:
      newState.tableSelected = action.payload;
      break;
    case SET_SCORES:
      {
        const { name } = action.payload;
        newState.scoresState[name] = action.payload.score;

        const isByObservationMethod = newState.methodSelected === observation;

        let isReady;
        if (isByObservationMethod) {
          isReady =
            Object.keys(newState.scoresState).length ===
            Object.keys(newState.scoresSubcategory.objective).length;
        } else {
          isReady =
            Object.keys(newState.scoresState).length ===
            Object.keys([
              ...newState.scoresSubcategory.subjective,
              ...newState.scoresSubcategory.objective,
            ]).length;
        }

        if (isReady) {
          newState.allValuesEntered = true;
        }
      }
      break;
    case RESET_SCORES:
      newState.scoresState = {};
      break;
    case SET_ALL_VALUES_ENTERED:
      newState.allValuesEntered = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const MeasuresContext = createContext<MeasuresContext>(
  initMeasuresContext as MeasuresContext,
);
export const MeasuresContextProvider = ({
  children,
}: MeasuresContextProviderProps) => {
  const [
    {
      editingMeasure,
      newMeasure,
      loadingOverlay,
      measureSelected,
      tableSelected,
      nameSelected,
      showTable,
      methodSelected,
      scoresState,
      allValuesEntered,
      scoresSubcategory,
      isLoading,
    },
    dispatch,
  ] = useReducer(MeasuresContextReducer, initialState);

  return (
    <MeasuresContext.Provider
      value={{
        editingMeasure,
        measureSelected,
        tableSelected,
        methodSelected,
        nameSelected,
        newMeasure,
        loadingOverlay,
        showTable,
        scoresState,
        allValuesEntered,
        anyValueEntered: scoresState && !!Object.keys(scoresState).length,
        scoresSubcategory,
        isLoading,
        dispatch,
      }}
    >
      {children}
    </MeasuresContext.Provider>
  );
};

export default MeasuresContextProvider;
